import { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';

import { Button } from 'ui';
import Badge from 'ui/Badge';
import Card from 'ui/Card';

import { useSelector } from 'react-redux';
import { IRootStore } from 'store/Root.reducer';
import { UsersApi } from 'services';
import { subscriptionApi, SubscriptionResponse, SubscriptionPlan, SubscriptionPlansResponse } from 'services/subscription';
import { ObjectSensorType } from 'entities/common';

const SubscriptionView = () => {
  const userId = useSelector((state: IRootStore) => state.auth.user?.id);
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlansResponse | null>(null);
  const [unpaidSensorIds, setUnpaidSensorIds] = useState<(string | number)[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingCheckout, setProcessingCheckout] = useState<SubscriptionPlan | null>(null);
  const [processingPortal, setProcessingPortal] = useState(false);
  const [processingAddSensors, setProcessingAddSensors] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setProcessingCheckout(null);
      setProcessingPortal(false);
      setProcessingAddSensors(false);

      const [subData, plansData] = await Promise.all([
        subscriptionApi.getSubscription(),
        subscriptionApi.getPlans(),
      ]);
      setSubscription(subData);
      setPlans(plansData);

      if (userId && subData.has_access) {
        const hierarchyData = await UsersApi.getDashboard(userId);
        const sensors: ObjectSensorType[] = [];

        hierarchyData.data.objects?.forEach((obj: any) => {
          obj.sensors?.forEach((sensor: ObjectSensorType) => {
            sensors.push(sensor);
          });
          obj.zones?.forEach((zone: any) => {
            zone.sensors?.forEach((sensor: ObjectSensorType) => {
              sensors.push(sensor);
            });
          });
        });

        const unpaidIds = sensors
          .filter((sensor) => sensor.is_paid === false)
          .map((sensor) => sensor.id);

        setUnpaidSensorIds(unpaidIds);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load subscription data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        fetchData();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [userId]);

  const handleCheckout = async (plan: SubscriptionPlan) => {
    try {
      setProcessingCheckout(plan);
      setError(null);
      const response = await subscriptionApi.createCheckoutSession(plan);
      window.location.href = response.url;
    } catch (err: any) {
      setError(err?.message || 'Failed to start checkout');
      console.error('Error creating checkout session:', err);
      setProcessingCheckout(null);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setProcessingPortal(true);
      setError(null);
      const response = await subscriptionApi.getPortalUrl();
      window.location.href = response.url;
    } catch (err: any) {
      setError(err?.message || 'Failed to open billing portal');
      console.error('Error getting portal URL:', err);
      setProcessingPortal(false);
    }
  };

  const handleAddSensors = async () => {
    if (!subscription || unpaidSensorIds.length === 0) return;

    try {
      setProcessingAddSensors(true);
      setError(null);
      await subscriptionApi.addSensors(unpaidSensorIds);
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to add sensors');
      console.error('Error adding sensors:', err);
      setProcessingAddSensors(false);
    }
  };

  const isSubscribed = subscription?.has_access || subscription?.status === 'active';
  const isCancelled = subscription?.cancel_at_period_end;
  const isExpired =
    !subscription?.has_access &&
    subscription?.state === 'inactive' &&
    subscription?.status !== 'active';
  const isUnsubscribed =
    (!subscription?.has_access && subscription?.state === 'none') ||
    (!isSubscribed && !isCancelled && !isExpired);

  const sensorDifference = unpaidSensorIds.length;
  const showAttention = (isSubscribed || isCancelled) && sensorDifference > 0;

  const proratedAmount = useMemo(() => {
    if (!subscription || !plans || !showAttention) return 0;

    const currentPlanDetails = plans.plans[subscription.plan];
    if (!currentPlanDetails) return 0;

    const unitAmount = currentPlanDetails.unit_amount / 100;
    const intervalCount = currentPlanDetails.interval_count || 1;
    const monthsInPeriod = currentPlanDetails.interval === 'year' ? intervalCount * 12 : intervalCount;

    const perSensorMonthPrice = unitAmount / monthsInPeriod;

    if (!subscription.current_period_start || !subscription.current_period_end) return 0;

    const periodStart = new Date(subscription.current_period_start);
    const periodEnd = new Date(subscription.current_period_end);
    const now = new Date();

    const totalDays = Math.round((periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24));
    const daysLeft = Math.max(1, Math.ceil((periodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

    const prorateFactor = daysLeft / totalDays;
    const chargeAmount = perSensorMonthPrice * prorateFactor * sensorDifference;

    return Math.max(1, Math.round(chargeAmount));
  }, [subscription, plans, sensorDifference, showAttention]);

  const { title, description } = useMemo(() => {
    if (isUnsubscribed) {
      return {
        title: 'Please subscribe',
        description: 'You need to subscribe to continue using the service',
      };
    }

    if (isExpired) {
      return {
        title: 'Subscription expired',
        description: 'Your subscription has expired. Please renew to continue using the service.',
      };
    }

    return {
      title: 'Subscription',
      description: 'Subscription price depends on the number of sensors installed for you',
    };
  }, [isUnsubscribed, isExpired]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const getPlanLabel = (plan: SubscriptionPlan) => {
    switch (plan) {
      case 'monthly':
        return 'Monthly';
      case 'semi_yearly':
        return 'Semi-yearly';
      case 'yearly':
        return 'Yearly';
      default:
        return plan;
    }
  };

  if (loading) {
    return (
      <div className="subscription-view">
        <div className="subscription-view__header">
          <h1>Loading...</h1>
          <p>Please wait while we load your subscription information</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-view">
      <div className="subscription-view__header">
        <h1>{title}</h1>
        <p>{description}</p>
      </div>

      {error && (
        <div className="subscription-view__content">
          <Card type='error' className='subscription-view__card-attention'>
            <h2>Error</h2>
            <p>{error}</p>
          </Card>
        </div>
      )}

      <div className="subscription-view__content">
        {(isSubscribed || isCancelled) && subscription && (
          <Card className='subscription-view__card-sensors'>
            <span>Your current number of sensors</span>
            <p>{subscription.sensors_count}</p>
          </Card>
        )}

        {(isSubscribed || isCancelled) && subscription && (
          <Card className='subscription-view__card-plan'>
            <h1 className='subscription-view__card-plan-title'>
              Current plan: <span>{getPlanLabel(subscription.plan)}</span>
            </h1>

            <span className="subscription-view__card-plan-description">
              Streamlining your day-to-day so you can focus on what you love and engage your
              community.
            </span>

            {subscription.current_period_end && (
              <p className='subscription-view__card-plan-renew'>
                {isCancelled ? 'Expires' : 'Renew'} at {formatDate(subscription.current_period_end)}
              </p>
            )}

            {isCancelled && (
              <Badge
                type="error"
                title="Cancelled"
                className="subscription-view__card-plan-badge"
              />
            )}

            <Button
              className='subscription-view__card-plan-button'
              onClick={handleManageSubscription}
              disabled={processingPortal}
            >
              {processingPortal ? 'Loading...' : 'Manage subscription'}
            </Button>
          </Card>
        )}

        {isExpired && subscription && (
          <Card className='subscription-view__card-plan'>
            <h1 className='subscription-view__card-plan-title'>
              Last plan: <span>{getPlanLabel(subscription.plan)}</span>
            </h1>

            <span className="subscription-view__card-plan-description">
              Your subscription period has ended. Renew to regain access.
            </span>

            {subscription.current_period_end && (
              <p className='subscription-view__card-plan-renew'>
                Expired at {formatDate(subscription.current_period_end)}
              </p>
            )}

            <Badge
              type="error"
              title="Expired"
              className="subscription-view__card-plan-badge"
            />

            <Button
              className='subscription-view__card-plan-button'
              onClick={handleManageSubscription}
              disabled={processingPortal}
            >
              {processingPortal ? 'Loading...' : 'Renew subscription'}
            </Button>
          </Card>
        )}

        {showAttention && subscription && (
          <Card className='subscription-view__card-attention'>
            <h2>Attention!</h2>
            <p>
              You have {sensorDifference} new {sensorDifference === 1 ? 'sensor' : 'sensors'} added to your account. To continue using these sensors, please complete the payment for the remaining part of the current billing period.
            </p>
            <p>
              The amount will be calculated proportionally based on the number of days left until your next billing date.
            </p>
            <p>
              Starting from the next billing cycle, your subscription will automatically include the cost for the updated total number of sensors.
            </p>

            <h2 className='subscription-view__card-attention-amount'>{proratedAmount} AED</h2>

            <Button
              className='subscription-view__card-attention-button'
              onClick={handleAddSensors}
              disabled={processingAddSensors}
            >
              {processingAddSensors ? 'Processing...' : 'Pay now'}
            </Button>

          </Card>
        )}

        {isUnsubscribed && plans && (
          <div className="subscription-view__card-plans">
            {(Object.entries(plans.plans) as [SubscriptionPlan, any][]).map(([key, plan]) => (
              <Card key={key} className='subscription-view__card-subscribe'>
                <h1 className='subscription-view__card-plan-title'>
                  <span>{getPlanLabel(key)}</span>
                </h1>

                <span className='subscription-view__card-plan-description'>
                  Streamlining your day-to-day so you can focus on what you love and engage your community.
                </span>

                <h2 className="subscription-view__card-plan-price">
                  {(plan.unit_amount / 100) * (subscription?.sensors_count || 1)} {plan.currency.toUpperCase()}
                </h2>

                <Button
                  className="subscription-view__card-plan-button"
                  onClick={() => handleCheckout(key)}
                  disabled={processingCheckout === key}
                >
                  {processingCheckout === key ? 'Processing...' : 'Subscribe now'}
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionView;
